import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Plus, Trash2, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { CentsInput, OverrideInput, Switch } from './bits';
import {
  ITEM_TYPES, TYPE_BLURB, TYPE_LABEL, byRank, emptyPrice, errMsg,
  type BundleSlot, type Category, type Item, type ItemRequest, type ItemType,
  type Modifier, type ModifierAttachment, type PizzaSize, type PizzaSizePrice, type PizzaTopping, type Price, type SlotOption,
} from './types';
import './menu.css';

/** Everything the editor needs to know about the store's catalog. */
type Vocab = {
  categories: Category[];
  modifiers: Modifier[];
  sizes: PizzaSize[];
  toppings: PizzaTopping[];
  items: Item[];
};

/** The working copy: every type's definition materialized; only the block
 * matching the type is sent. The type is locked after creation. */
type Draft = {
  itemType: ItemType;
  categoryId: string | null;
  name: string;
  description: string;
  soldOut: boolean;
  available: boolean;
  availablePickup: boolean;
  availableDelivery: boolean;
  availableDineIn: boolean;
  isGstApplicable: boolean;
  isAlcohol: boolean;
  imageUrl: string | null;
  modifiers: ModifierAttachment[];
  price: Price;
  /** bundle: a fixed price, or null = the sum of the picks */
  bundleFixedPrice: boolean;
  sizes: PizzaSizePrice[];
  defaultToppings: string[];
  halfSizeId: string;
  slots: BundleSlot[];
};

const freshDraft = (categoryId: string | null): Draft => ({
  itemType: 'generic',
  categoryId,
  name: '',
  description: '',
  soldOut: false,
  available: true,
  availablePickup: true,
  availableDelivery: true,
  availableDineIn: true,
  isGstApplicable: true,
  isAlcohol: false,
  imageUrl: null,
  modifiers: [],
  price: emptyPrice(),
  bundleFixedPrice: true,
  sizes: [],
  defaultToppings: [],
  halfSizeId: '',
  slots: [],
});

function draftOf(item: Item): Draft {
  const d: Draft = {
    ...freshDraft(item.categoryId),
    itemType: item.itemType,
    name: item.name,
    description: item.description ?? '',
    soldOut: item.soldOut,
    available: item.available,
    availablePickup: item.availablePickup,
    availableDelivery: item.availableDelivery,
    availableDineIn: item.availableDineIn,
    isGstApplicable: item.isGstApplicable,
    isAlcohol: item.isAlcohol,
    imageUrl: item.imageUrl,
    modifiers: item.modifiers,
  };
  switch (item.itemType) {
    case 'generic':
      d.price = item.price;
      break;
    case 'pizza':
      d.sizes = item.sizes;
      d.defaultToppings = item.defaultToppings;
      break;
    case 'half_and_half':
      d.halfSizeId = item.sizeId;
      break;
    case 'bundle':
      d.bundleFixedPrice = item.price !== null;
      d.price = item.price ?? emptyPrice();
      d.slots = item.slots;
      break;
  }
  return d;
}

const freshSlot = (slotType: 'generic' | 'pizza'): BundleSlot => ({
  slotId: null,
  slotType,
  name: '',
  minSelect: 1,
  maxSelect: 1,
  allowRepeat: false,
  allowHalfAndHalf: slotType === 'pizza' ? false : null,
  sizeId: null,
  options: [],
});

export function ItemEditor() {
  const { rid, sid } = useScope();
  const base = useBase();
  const nav = useNavigate();
  const { itemId = 'new' } = useParams();
  const [search] = useSearchParams();
  const isNew = itemId === 'new';
  const ui = `/r/${rid}/s/${sid}/menu`;

  const [vocab, setVocab] = useState<Vocab | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [categories, modifiers, sizes, toppings, items, item] = await Promise.all([
        api.get<Category[]>(`${base}/categories`),
        api.get<Modifier[]>(`${base}/modifiers`),
        api.get<PizzaSize[]>(`${base}/pizza-sizes`),
        api.get<PizzaTopping[]>(`${base}/pizza-toppings`),
        api.get<Item[]>(`${base}/items`),
        isNew ? Promise.resolve(null) : api.get<Item>(`${base}/items/${itemId}`),
      ]);
      setVocab({
        categories: categories.sort(byRank),
        modifiers: modifiers.sort(byRank),
        sizes: sizes.sort(byRank),
        toppings: toppings.sort(byRank),
        items: items.sort(byRank),
      });
      setDraft((prev) => prev ?? (item ? draftOf(item) : freshDraft(search.get('categoryId'))));
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'Failed to load the item.'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, itemId, isNew]);

  useEffect(() => {
    setDraft(null);
    void load();
  }, [load]);

  if (!vocab || !draft) {
    return (
      <>
        {error && <div className="err">{error}</div>}
        <div className="loading">Loading…</div>
      </>
    );
  }

  const d = draft;
  const set = (patch: Partial<Draft>) => setDraft({ ...d, ...patch });
  const pizzas = vocab.items.filter((i) => i.itemType === 'pizza' && i.itemId !== itemId);
  const generics = vocab.items.filter((i) => i.itemType === 'generic' && i.itemId !== itemId);
  const sizeName = (id: string | null) => vocab.sizes.find((s) => s.sizeId === id)?.name ?? '';
  const halfEligible = d.halfSizeId
    ? pizzas.filter((p) => p.itemType === 'pizza' && p.sizes.some((s) => s.sizeId === d.halfSizeId && s.halfAndHalfAvailable))
    : [];

  function validate(): string | null {
    if (!d.name.trim()) return 'The item needs a name.';
    if (d.itemType === 'pizza' && d.sizes.length === 0) return 'A pizza needs at least one size ticked.';
    if (d.itemType === 'half_and_half' && !d.halfSizeId) return 'Pick the half & half size.';
    if (d.itemType === 'bundle') {
      if (d.slots.length === 0) return 'A bundle needs at least one slot.';
      for (const s of d.slots) {
        if (!s.name.trim()) return 'Every slot needs a name.';
        if (s.options.length === 0) return `Slot “${s.name}” needs at least one option.`;
        if (s.options.some((o) => !o.itemId)) return `Slot “${s.name}” has an option with no item picked.`;
        if (s.slotType === 'pizza' && !s.sizeId) return `Pizza slot “${s.name}” needs a size.`;
        if (s.minSelect > s.maxSelect) return `Slot “${s.name}”: min cannot exceed max.`;
      }
    }
    return null;
  }

  function body(): ItemRequest {
    const common = {
      categoryId: d.categoryId,
      name: d.name.trim(),
      description: d.description.trim() || null,
      soldOut: d.soldOut,
      available: d.available,
      availablePickup: d.availablePickup,
      availableDelivery: d.availableDelivery,
      availableDineIn: d.availableDineIn,
      isGstApplicable: d.isGstApplicable,
      isAlcohol: d.isAlcohol,
      imageUrl: d.imageUrl,
      modifiers: d.modifiers,
    };
    switch (d.itemType) {
      case 'generic':
        return { ...common, itemType: 'generic', price: d.price };
      case 'pizza':
        return { ...common, itemType: 'pizza', sizes: d.sizes, defaultToppings: d.defaultToppings };
      case 'half_and_half':
        return { ...common, itemType: 'half_and_half', sizeId: d.halfSizeId };
      case 'bundle':
        return {
          ...common,
          itemType: 'bundle',
          price: d.bundleFixedPrice ? d.price : null,
          slots: d.slots.map((s) => ({
            ...s,
            name: s.name.trim(),
            allowHalfAndHalf: s.slotType === 'pizza' ? (s.allowHalfAndHalf ?? false) : null,
            sizeId: s.slotType === 'pizza' ? s.sizeId : null,
          })),
        };
    }
  }

  async function save() {
    const problem = validate();
    if (problem) return setError(problem);
    setBusy(true);
    try {
      if (isNew) await api.post<Item>(`${base}/items`, body());
      else await api.put<Item>(`${base}/items/${itemId}`, body());
      nav(ui);
    } catch (e) {
      setError(errMsg(e, 'Save failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete “${d.name}”? This can’t be undone.`)) return;
    setBusy(true);
    try {
      await api.del(`${base}/items/${itemId}`);
      nav(ui);
    } catch (e) {
      setError(errMsg(e, 'Delete failed — the item may still be offered by a bundle.'));
    } finally {
      setBusy(false);
    }
  }

  // ── satellite editing helpers ─────────────────────────────────────

  const sizeEntry = (sizeId: string) => d.sizes.find((s) => s.sizeId === sizeId);
  const toggleSize = (sizeId: string) =>
    set({
      sizes: sizeEntry(sizeId)
        ? d.sizes.filter((s) => s.sizeId !== sizeId)
        : [...d.sizes, { sizeId, ...emptyPrice(), halfAndHalfAvailable: true }],
    });
  const patchSize = (sizeId: string, patch: Partial<PizzaSizePrice>) =>
    set({ sizes: d.sizes.map((s) => (s.sizeId === sizeId ? { ...s, ...patch } : s)) });
  const patchSlot = (i: number, patch: Partial<BundleSlot>) =>
    set({ slots: d.slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  const priceFields = (p: Price, onChange: (next: Price) => void) => (
    <div className="mn-fields">
      <div className="field">
        <label>Base price</label>
        <CentsInput value={p.priceInCents} title="Base price" onChange={(c) => onChange({ ...p, priceInCents: c })} />
      </div>
      <div className="mn-size-extras">
        <span className="lbl">Extras:</span>
        <span className="lbl">pickup</span>
        <CentsInput value={p.pickupExtraInCents} onChange={(c) => onChange({ ...p, pickupExtraInCents: c })} />
        <span className="lbl">delivery</span>
        <CentsInput value={p.deliveryExtraInCents} onChange={(c) => onChange({ ...p, deliveryExtraInCents: c })} />
        <span className="lbl">dine-in</span>
        <CentsInput value={p.dineInExtraInCents} onChange={(c) => onChange({ ...p, dineInExtraInCents: c })} />
        <span className="lbl">uber eats</span>
        <CentsInput value={p.uberEatsExtraInCents} onChange={(c) => onChange({ ...p, uberEatsExtraInCents: c })} />
      </div>
      <div className="mn-hint">Per-channel extras are added on top of the base price.</div>
    </div>
  );

  const optionEditor = (options: SlotOption[], pool: Item[], onChange: (next: SlotOption[]) => void) => (
    <>
      {options.map((o, i) => (
        <div key={i} className="mn-opt-row">
          <select className="input" value={o.itemId} aria-label="Option item"
            onChange={(e) => onChange(options.map((x, j) => (j === i ? { ...x, itemId: e.target.value } : x)))}>
            <option value="">Pick an item…</option>
            {pool.map((p) => (
              <option key={p.itemId} value={p.itemId}>{p.name}</option>
            ))}
          </select>
          <span className="mn-hint" style={{ whiteSpace: 'nowrap' }}>+ extra</span>
          <CentsInput value={o.extraChargeInCents}
            onChange={(c) => onChange(options.map((x, j) => (j === i ? { ...x, extraChargeInCents: c } : x)))} />
          <label className="mn-check" title="Pre-selected for the customer">
            <input type="checkbox" checked={o.isDefault}
              onChange={(e) => onChange(options.map((x, j) => (j === i ? { ...x, isDefault: e.target.checked } : x)))} />
            default
          </label>
          <button className="mn-iconbtn danger" title="Remove option" onClick={() => onChange(options.filter((_, j) => j !== i))}>
            <X size={15} />
          </button>
        </div>
      ))}
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }}
        onClick={() => onChange([...options, { itemId: '', extraChargeInCents: 0, isDefault: options.length === 0 }])}>
        <Plus size={14} /> Add option
      </button>
    </>
  );

  const flag = (label: string, sub: string, on: boolean, patch: (v: boolean) => Partial<Draft>) => (
    <div className="mn-flag">
      <div className="grow">
        <b>{label}</b>
        <span className="sub">{sub}</span>
      </div>
      <Switch on={on} title={label} onToggle={() => set(patch(!on))} />
    </div>
  );

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Store · Menu · {isNew ? 'New item' : TYPE_LABEL[d.itemType] ?? 'Item'}</div>
          <h1>{isNew ? 'New item' : d.name || 'Item'}</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!isNew && (
            <button className="btn btn-danger" disabled={busy} onClick={() => void remove()}>
              <Trash2 size={15} /> Delete
            </button>
          )}
          <Link className="btn btn-outline" to={ui}>Cancel</Link>
          <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {isNew ? 'Create item' : 'Save changes'}
          </button>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <div className="panel" style={{ marginBottom: 20 }}>
        <h2>Item type{!isNew ? ' · locked after creation' : ''}</h2>
        <div className="mn-types">
          {ITEM_TYPES.map((t) => (
            <button key={t} className={'mn-type' + (d.itemType === t ? ' on' : '')} disabled={!isNew} aria-label={`Type: ${TYPE_LABEL[t]}`}
              onClick={() => set({ itemType: t })}>
              <b>{TYPE_LABEL[t]}</b>
              <span>{TYPE_BLURB[t]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mn-editor-grid">
        <div className="mn-col">
          <div className="panel">
            <h2>Basics</h2>
            <div className="mn-fields">
              <div className="field">
                <label htmlFor="item-name">Name</label>
                <input id="item-name" className="input" value={d.name} placeholder="e.g. Margherita"
                  onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="item-description">Description</label>
                <input id="item-description" className="input" value={d.description} placeholder="Shown to customers (optional)"
                  onChange={(e) => set({ description: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="item-category">Category</label>
                <select id="item-category" className="input" value={d.categoryId ?? ''}
                  onChange={(e) => set({ categoryId: e.target.value || null })}>
                  <option value="">Uncategorised</option>
                  {vocab.categories.map((c) => (
                    <option key={c.categoryId} value={c.categoryId}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="item-image">Image URL</label>
                <input id="item-image" className="input" value={d.imageUrl ?? ''} placeholder="https://… (optional)"
                  onChange={(e) => set({ imageUrl: e.target.value.trim() || null })} />
              </div>
            </div>
          </div>

          {d.itemType === 'generic' && (
            <div className="panel">
              <h2>Price</h2>
              {priceFields(d.price, (price) => set({ price }))}
            </div>
          )}

          {d.itemType === 'pizza' && (
            <div className="panel">
              <h2>Sizes & pricing</h2>
              {vocab.sizes.length === 0 && (
                <div className="mn-hint">
                  No pizza sizes yet — create them in <Link to={`${ui}/pizza-settings`}>Pizza settings</Link> first.
                </div>
              )}
              {vocab.sizes.map((s) => {
                const e = sizeEntry(s.sizeId);
                return (
                  <div key={s.sizeId} className="mn-flag" style={{ alignItems: 'flex-start' }}>
                    <label className="mn-check" style={{ minWidth: 140, paddingTop: 6 }}>
                      <input type="checkbox" checked={!!e} onChange={() => toggleSize(s.sizeId)} />
                      {s.name}
                    </label>
                    {e && (
                      <div className="grow">
                        <div className="mn-size-extras">
                          <span className="lbl">price</span>
                          <CentsInput value={e.priceInCents} title={`${s.name} price`} onChange={(c) => patchSize(s.sizeId, { priceInCents: c })} />
                          <span className="lbl">pickup +</span>
                          <CentsInput value={e.pickupExtraInCents} onChange={(c) => patchSize(s.sizeId, { pickupExtraInCents: c })} />
                          <span className="lbl">delivery +</span>
                          <CentsInput value={e.deliveryExtraInCents} onChange={(c) => patchSize(s.sizeId, { deliveryExtraInCents: c })} />
                          <span className="lbl">dine-in +</span>
                          <CentsInput value={e.dineInExtraInCents} onChange={(c) => patchSize(s.sizeId, { dineInExtraInCents: c })} />
                          <span className="lbl">uber eats +</span>
                          <CentsInput value={e.uberEatsExtraInCents} onChange={(c) => patchSize(s.sizeId, { uberEatsExtraInCents: c })} />
                        </div>
                        <label className="mn-check" style={{ marginTop: 6 }}>
                          <input type="checkbox" checked={e.halfAndHalfAvailable}
                            onChange={(ev) => patchSize(s.sizeId, { halfAndHalfAvailable: ev.target.checked })} />
                          available for half & half at this size
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
              <h2 style={{ marginTop: 16 }}>Default toppings</h2>
              {vocab.toppings.length === 0 && <div className="mn-hint">No toppings in the store catalog yet.</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', paddingTop: 8 }}>
                {vocab.toppings.map((t) => (
                  <label key={t.toppingId} className="mn-check">
                    <input type="checkbox" checked={d.defaultToppings.includes(t.toppingId)}
                      onChange={(e) =>
                        set({
                          defaultToppings: e.target.checked
                            ? [...d.defaultToppings, t.toppingId]
                            : d.defaultToppings.filter((x) => x !== t.toppingId),
                        })
                      } />
                    {t.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {d.itemType === 'half_and_half' && (
            <div className="panel">
              <h2>Half & half</h2>
              <div className="mn-fields">
                <div className="field" style={{ maxWidth: 280 }}>
                  <label htmlFor="hah-size">Size</label>
                  <select id="hah-size" className="input" value={d.halfSizeId} onChange={(e) => set({ halfSizeId: e.target.value })}>
                    <option value="">Pick a size…</option>
                    {vocab.sizes.map((s) => (
                      <option key={s.sizeId} value={s.sizeId}>
                        {s.name}{!s.halfAndHalfEnabled ? ' (half & half disabled)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mn-hint">
                  Every pizza sold at this size with “available for half & half” ticked is eligible
                  {d.halfSizeId && ` — ${halfEligible.length} right now${halfEligible.length ? `: ${halfEligible.map((p) => p.name).join(', ')}` : ''}`}.
                  The split charge comes from the size.
                </div>
              </div>
            </div>
          )}

          {d.itemType === 'bundle' && (
            <>
              <div className="panel">
                <h2>Bundle price</h2>
                <label className="mn-check" style={{ marginBottom: 10 }}>
                  <input type="checkbox" checked={d.bundleFixedPrice} onChange={(e) => set({ bundleFixedPrice: e.target.checked })} />
                  Fixed price (otherwise the picks are summed)
                </label>
                {d.bundleFixedPrice && priceFields(d.price, (price) => set({ price }))}
              </div>
              <div className="panel">
                <h2>Slots</h2>
                <div className="mn-hint">Generic slots offer generic items; pizza slots offer pizzas at one size.</div>
                {d.slots.map((s, i) => (
                  <div key={i} className="mn-step">
                    <div className="mn-step-head">
                      <span className="src">{s.slotType === 'pizza' ? 'Pizza slot' : 'Generic slot'}</span>
                      <input className="input" value={s.name} placeholder={s.slotType === 'pizza' ? 'e.g. Pick your pizza' : 'e.g. Choose a drink'}
                        aria-label="Slot name" onChange={(e) => patchSlot(i, { name: e.target.value })} />
                      {s.slotType === 'pizza' && (
                        <>
                          <select className="input" style={{ width: 'auto' }} value={s.sizeId ?? ''} aria-label="Slot size"
                            onChange={(e) => patchSlot(i, { sizeId: e.target.value || null })}>
                            <option value="">Size…</option>
                            {vocab.sizes.map((sz) => (
                              <option key={sz.sizeId} value={sz.sizeId}>{sz.name}</option>
                            ))}
                          </select>
                          <label className="mn-check" title="Let the customer split this pizza half & half">
                            <input type="checkbox" checked={s.allowHalfAndHalf ?? false}
                              onChange={(e) => patchSlot(i, { allowHalfAndHalf: e.target.checked })} />
                            half & half
                          </label>
                        </>
                      )}
                      <span className="lbl mn-hint">pick</span>
                      <OverrideInput value={s.minSelect} placeholder="min" onChange={(v) => patchSlot(i, { minSelect: v ?? 0 })} />
                      <span className="lbl mn-hint">–</span>
                      <OverrideInput value={s.maxSelect} placeholder="max" onChange={(v) => patchSlot(i, { maxSelect: v ?? 1 })} />
                      <label className="mn-check" title="The same option may be picked more than once">
                        <input type="checkbox" checked={s.allowRepeat} onChange={(e) => patchSlot(i, { allowRepeat: e.target.checked })} />
                        repeats
                      </label>
                      <button className="mn-iconbtn danger" title="Remove slot" onClick={() => set({ slots: d.slots.filter((_, j) => j !== i) })}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                    {optionEditor(s.options, s.slotType === 'pizza' ? pizzas : generics, (next) => patchSlot(i, { options: next }))}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => set({ slots: [...d.slots, freshSlot('generic')] })}>
                    <Plus size={14} /> Generic slot
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => set({ slots: [...d.slots, freshSlot('pizza')] })}>
                    <Plus size={14} /> Pizza slot
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mn-col">
          <div className="panel">
            <h2>Availability</h2>
            {flag('Available', 'Master switch — off hides it everywhere', d.available, (v) => ({ available: v }))}
            {flag('Pickup', 'Orderable on the pickup channel', d.availablePickup, (v) => ({ availablePickup: v }))}
            {flag('Delivery', 'Orderable on the delivery channel', d.availableDelivery, (v) => ({ availableDelivery: v }))}
            {flag('Dine-in', 'Orderable at the table', d.availableDineIn, (v) => ({ availableDineIn: v }))}
            {flag('Sold out', 'The daily 86 — back tomorrow', d.soldOut, (v) => ({ soldOut: v }))}
          </div>

          <div className="panel">
            <h2>Tax & flags</h2>
            {flag('GST applies', 'Included in the 10% GST calculation', d.isGstApplicable, (v) => ({ isGstApplicable: v }))}
            {flag('Alcohol', 'Flagged for responsible-service rules', d.isAlcohol, (v) => ({ isAlcohol: v }))}
          </div>

          <div className="panel">
            <h2>Modifier groups</h2>
            {vocab.modifiers.length === 0 && (
              <div className="mn-hint">No modifier groups yet — create them in <Link to={`${ui}/modifiers`}>Modifier groups</Link>.</div>
            )}
            {vocab.modifiers.map((m) => {
              const att = d.modifiers.find((x) => x.modifierId === m.modifierId);
              return (
                <div key={m.modifierId} className="mn-flag">
                  <label className="mn-check grow" style={{ alignItems: 'flex-start' }}>
                    <input type="checkbox" checked={!!att} style={{ marginTop: 4 }}
                      onChange={(e) =>
                        set({
                          modifiers: e.target.checked
                            ? [...d.modifiers, { modifierId: m.modifierId, minSelectOverride: null, maxSelectOverride: null }]
                            : d.modifiers.filter((x) => x.modifierId !== m.modifierId),
                        })
                      } />
                    <span>
                      {m.name}
                      <span className="sub" style={{ display: 'block' }}>
                        {m.options.map((o) => o.name).join(', ')} · pick {m.minSelect}–{m.maxSelect}
                      </span>
                    </span>
                  </label>
                  {att && (
                    <>
                      <OverrideInput value={att.minSelectOverride} placeholder={`${m.minSelect}`}
                        onChange={(v) => set({ modifiers: d.modifiers.map((x) => (x.modifierId === m.modifierId ? { ...x, minSelectOverride: v } : x)) })} />
                      <OverrideInput value={att.maxSelectOverride} placeholder={`${m.maxSelect}`}
                        onChange={(v) => set({ modifiers: d.modifiers.map((x) => (x.modifierId === m.modifierId ? { ...x, maxSelectOverride: v } : x)) })} />
                    </>
                  )}
                </div>
              );
            })}
            <div className="mn-hint">Blank overrides use the group's own min/max.</div>
          </div>
          {!isNew && (
            <div className="panel">
              <h2>Where it is offered</h2>
              <div className="mn-hint">
                {(() => {
                  const hosts = vocab.items.filter((i) => i.itemType === 'bundle' && i.slots.some((s) => s.options.some((o) => o.itemId === itemId)));
                  return hosts.length ? `Bundles: ${hosts.map((h) => h.name).join(', ')}` : 'Not offered inside any bundle.';
                })()}
                {d.itemType === 'pizza' && ` · Size ${sizeName(d.sizes[0]?.sizeId ?? null) || '—'} and ${Math.max(0, d.sizes.length - 1)} more.`}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
